import { inject, Injectable } from '@angular/core';

import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { BsHubService } from '../Hub/bs-hub.service';
import { FirebaseService, ProfileService, AmplitudeService } from '@brightside-web/desktop/data-access/shared';
import { Environment } from '@micro-core/environment';

export type SignInAnalyticsType = 'password' | 'sso' | 'sign up';

export const PENDING_SIGN_IN_TYPE_KEY = 'PENDING_SIGN_IN_TYPE';

/** Persists last completed/started sign-in type (localStorage; survives Auth0 redirect). */
export const LAST_SIGN_IN_TYPE_KEY = 'brightside_last_sign_in_type';

export interface LoginOptions {
  connection?: string;
  trackSignInAnalytics?: boolean;
}

export interface UserAttributesInterface {
  guid: string;
  intercom_external_id: string;
  last_name: string;
  first_name: string;
  phone_number: string;
  email: string;
  locale: string;
  company: string;
  sf_contact_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class BsAuthService {
  private _reloginErrors: string[] = ['Unknown or invalid refresh token.'];
  private _auth0Service: Auth0Service = inject(Auth0Service);
  private _bsHubService: BsHubService = inject(BsHubService);
  private _profileService: ProfileService = inject(ProfileService);
  private _analytics: FirebaseService = inject(FirebaseService);
  private _amplitudeService: AmplitudeService = inject(AmplitudeService);

  private _userLoggedInSubject = new BehaviorSubject<boolean>(false);
  private _userLoggedOutSubject = new BehaviorSubject<boolean>(false);

  private _defaultScope = 'openid profile email phone_number read:current_user read:current_user_metadata offline_access';

  private storedAccessToken: string;
  private overrideIsAuthenticated: boolean | null = null;
  private _unloadLogoutRegistered = false;
  private _isNavigatingToAuth0 = false;
  private userId: string | undefined;

  static consumePendingSignInType(): SignInAnalyticsType | null {
    const value = sessionStorage.getItem(PENDING_SIGN_IN_TYPE_KEY);
    if (!value) {
      return null;
    }
    sessionStorage.removeItem(PENDING_SIGN_IN_TYPE_KEY);
    if (value === 'password' || value === 'sso' || value === 'sign up') {
      return value;
    }
    return null;
  }

  static getLastSignInType(): SignInAnalyticsType | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const value = localStorage.getItem(LAST_SIGN_IN_TYPE_KEY);
    if (value === 'password' || value === 'sso' || value === 'sign up') {
      return value;
    }
    return null;
  }

  static clearLastSignInType(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(LAST_SIGN_IN_TYPE_KEY);
  }

  constructor(private _env: Environment) {
    this.isAuthenticated().subscribe(authenticated => {
      if (authenticated) {
        this._registerUnloadLogout();
        this.notifyUserLoggedIn();
      } else {
        this.notifyUserLoggedOut();
      }
    });
    this._auth0Service.error$.subscribe((errors) => {
      /** Both branches below are drastic and both were silent. The logout branch returns the
       *  client to `window.location.origin`, which the catch-all route resolves to
       *  `registration/create-account` — the sign in screen. That makes this the only in-app path
       *  that can strand an already-authenticated client there, which is what MW-11053 reports
       *  for Change Password, and there was nothing anywhere to attribute it to. Reported at
       *  warning level before acting so the triggering Auth0 error is identifiable; the branching
       *  itself is unchanged.
       */
      Sentry.captureException(errors, {
        level: 'warning',
        tags: { auth0Error: this._reloginErrors.includes(errors?.message) ? 'relogin' : 'logout' },
        extra: { path: window.location.pathname, message: errors?.message },
      });

      if (this._reloginErrors.includes(errors.message)) {
        this.login('login', '', { trackSignInAnalytics: false });
      } else {
        /** errors logging in don't always result in the
         *  Auth0 information clearing, so we log out.
         */
        this.logout().subscribe();
      }
    });
  }

  /**
   * Ends the Auth0 session once the client leaves the app. MW-6082 moved tokens to memory only,
   * so without this a returning visitor is silently signed back in by a surviving Auth0 session.
   *
   * Skipped while we are the ones navigating to Auth0. `loginWithRedirect` unloads the page, so
   * this beacon fired on the way to `/authorize` and destroyed the session microseconds before
   * the request landed. Auth0 then had no session to honour and served `/u/login/identifier`
   * instead of the screen the request asked for — which is MW-11053: Change Password asks the
   * client to sign in again rather than showing the Auth0 password screen. `changePhoneNumber`
   * and `changeEmailAddress` go through the same `login()` path and were affected identically.
   *
   * Registered once. `isAuthenticated()` can emit `true` more than once and the listener was
   * anonymous, so every emission stacked another beacon that could never be removed.
   */
  private _registerUnloadLogout(): void {
    if (this._unloadLogoutRegistered) {
      return;
    }
    this._unloadLogoutRegistered = true;

    window.addEventListener('beforeunload', () => {
      if (this._isNavigatingToAuth0) {
        return;
      }
      const logoutUrl = `https://${this._env.auth0.domain}/v2/logout?client_id=${this._env.auth0.clientId}&returnTo=${encodeURIComponent(window.location.origin)}`;
      new Image().src = logoutUrl;
    });
  }

  getUserLoggedInEvent(): Observable<boolean> {
    return this._userLoggedInSubject.asObservable();
  }

  getUserLoggedOutEvent(): Observable<boolean> {
    return this._userLoggedOutSubject.asObservable();
  }

  notifyUserLoggedIn() {
    this._userLoggedInSubject.next(true);
    this._userLoggedOutSubject.next(false);
    this._bsHubService.dispatch('bsAuth', { event: 'signedIn' });
  }

  notifyUserLoggedOut() {
    this._userLoggedInSubject.next(false);
    this._userLoggedOutSubject.next(true);
    this._bsHubService.dispatch('bsAuth', { event: 'signedOut' });
  }

  login(
    prompt: 'login' | 'signup' | 'none' | string = 'login',
    additionalScope: string = '',
    options: LoginOptions = {}
  ): void {
    void this._loginAndRedirect(prompt, additionalScope, options);
  }

  private async _loginAndRedirect(
    prompt: 'login' | 'signup' | 'none' | string,
    additionalScope: string,
    options: LoginOptions
  ): Promise<void> {
    try {
      const { connection, trackSignInAnalytics = true } = options;
      const isSsoLogin = connection !== undefined;
      const connectionValue = connection?.trim() ?? '';
      this.overrideIsAuthenticated = null;

      if (trackSignInAnalytics) {
        this._trackSignInStarted(this._resolveSignInAnalyticsType(prompt, isSsoLogin));
        try {
          await this._amplitudeService.flush();
        } catch {
          // Proceed with redirect even if flush fails
        }
      }

      const authorizationParams: Record<string, string> = {
        screen_hint: prompt,
        scope: this._defaultScope + ' ' + additionalScope,
        redirect_uri: window.location.origin + '/authenticating',
      };

      if (connectionValue) {
        authorizationParams.connection = connectionValue;
      }

      // Suppress the unload logout beacon for this navigation — see _registerUnloadLogout. Set
      // immediately before the redirect so an early return above cannot leave it latched.
      this._isNavigatingToAuth0 = true;

      // Subscribed only to observe failure. `loginWithRedirect` returns `from(promise)` over a
      // promise the SDK has already created, so the redirect is issued eagerly whether or not
      // anyone subscribes — which also means a rejection never reaches the enclosing try/catch.
      // Left unobserved it would strand `_isNavigatingToAuth0` as true and suppress the unload
      // logout for the rest of the session, defeating MW-6082.
      this._auth0Service
        .loginWithRedirect({
          ...(isSsoLogin ? { appState: { loginType: 'sso' } } : {}),
          authorizationParams,
        })
        .subscribe({
          error: () => {
            this._isNavigatingToAuth0 = false;
          },
        });
    } catch {
      // login() fire-and-forgets this promise; swallow to avoid unhandled rejection. Unlatch so a
      // failed redirect that leaves the client on the page still ends the session on tab close.
      this._isNavigatingToAuth0 = false;
    }
  }

  loginWithSso(connection: string): void {
    this.login('login', '', { connection, trackSignInAnalytics: true });
  }

  logout(): Observable<void> {
    const logoutOptions = {
      logoutParams: {
        returnTo: window.location.origin,
      },
    };

    return new Observable<void>((observer) => {
      const logoutSubscription = this._auth0Service.logout(logoutOptions).subscribe({
        next: () => {
          this.overrideIsAuthenticated = null;
          observer.next();
          observer.complete();
        },
        error: (error: any) => {
          observer.error(error);
          console.error('Error logging out', error);
        },
      });
      return () => {
        logoutSubscription.unsubscribe();
      };
    });
  }

  isAuthenticated(): Observable<boolean> {
    if (typeof this.overrideIsAuthenticated === 'boolean') {
      return of(this.overrideIsAuthenticated);
    }
    return this._auth0Service.isAuthenticated$;
  }

  getToken(): Observable<string> {
    return this.isAuthenticated().pipe(
      switchMap((authenticated) => {
        if (authenticated) {
          if (this.storedAccessToken) return of(this.storedAccessToken);
          return this._auth0Service
            .getAccessTokenSilently({
              authorizationParams: {
                scope: 'openid profile email phone_number read:current_user read:current_user_metadata offline_access',
              },
            })
            .pipe(
              map((token) => token ?? ''),
              catchError((error) => {
                console.error('Error retrieving token:', error);
                return of('');
              })
            );
        } else {
          return of('');
        }
      })
    );
  }

  getIdTokenClaims() {
    return this._auth0Service.idTokenClaims$;
  }

  getUserFromAuth0(): Observable<User | null | undefined> {
    return this._auth0Service.user$;
  }

  getAuth0UserId$(): Observable<string | undefined> {
    return this._auth0Service.user$.pipe(
      map((user) => user?.sub),
      filter((sub): sub is string => !!sub),
      take(1)
    );
  }

  fetchUserAttributes(force = false): Observable<UserAttributesInterface> {
    return this._profileService.getProfile(force);
  }

  setAccessTokenForMicroApps(accessToken: string) {
    this.storedAccessToken = accessToken;
    this.overrideIsAuthenticated = true;
    this.notifyUserLoggedIn();
  }

  changePassword() {
    this.login('login', 'change_password', { trackSignInAnalytics: false });
  }

  changePhoneNumber() {
    this.login('login', 'change_phone_number', { trackSignInAnalytics: false });
  }

  changeEmailAddress() {
    this.login('login', 'change_email_address', { trackSignInAnalytics: false });
  }

  private _resolveSignInAnalyticsType(prompt: string, isSsoLogin = false): SignInAnalyticsType {
    if (isSsoLogin) {
      return 'sso';
    }
    if (prompt === 'signup') {
      return 'sign up';
    }
    return 'password';
  }

  private _trackSignInStarted(type: SignInAnalyticsType): void {
    this._analytics.logEvent('sign_in_started', { type });
    sessionStorage.setItem(PENDING_SIGN_IN_TYPE_KEY, type);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LAST_SIGN_IN_TYPE_KEY, type);
    }
  }
}
